/**
 * Typed fetchers for /api/v1/solutions — the shared job record of the Base
 * solutions (Translate, Q&A, Review, Quality) and the uploads a run consumes.
 *
 * Types come from the OpenAPI-generated schema. Regenerate via `pnpm gen:api`
 * after a backend Pydantic change.
 */

import type { components } from "./_generated/schema";
import { ApiError, apiFetch, type ApiErrorBody } from "./apiFetch";
import { downloadFile } from "./integration-browse";

export type RunOut = components["schemas"]["SolutionRunOut"];
export type RunDetailOut = components["schemas"]["SolutionRunDetailOut"];
export type RunListOut = components["schemas"]["SolutionRunListOut"];
export type RunProgressOut = components["schemas"]["SolutionRunProgressOut"];
export type RunInputsOut = components["schemas"]["SolutionRunInputsOut"];
export type CreateRunIn = components["schemas"]["CreateSolutionRunIn"];
export type RunCollectionIn = components["schemas"]["SolutionRunCollectionIn"];
export type UploadRefOut = components["schemas"]["SolutionUploadRefOut"];

export type RunKind = CreateRunIn["kind"];
export type RunStatusValue = RunOut["status"];

/** The resource type sharing uses for a run (`POST /shares`, read-only). */
export const SOLUTION_RUN_RESOURCE = "solution_run";

/** How often an active run is re-read. */
export const RUN_POLL_MS = 2000;

export function isTerminal(status: RunStatusValue): boolean {
  return status === "succeeded" || status === "failed";
}

const runBase = (id: string) => `/api/v1/solutions/runs/${encodeURIComponent(id)}`;

/** Grant-scoped: the caller's own runs plus those shared with them. */
export function listRuns(token: string, kind?: RunKind): Promise<RunListOut> {
  const qs = kind ? `?kind=${encodeURIComponent(kind)}` : "";
  return apiFetch<RunListOut>(`/api/v1/solutions/runs${qs}`, { token });
}

export function getRun(token: string, runId: string): Promise<RunDetailOut> {
  return apiFetch<RunDetailOut>(runBase(runId), { token });
}

/**
 * `201` with status `queued`. `404 document_index_not_found` for a collection
 * the caller cannot read; `404 solution_upload_not_found` for an unknown or
 * already-used upload; `400 solution_kind_unavailable` when the kind is not
 * deployed; `400 bad_request` for invalid inputs.
 */
export function createRun(token: string, input: CreateRunIn): Promise<RunDetailOut> {
  return apiFetch<RunDetailOut>("/api/v1/solutions/runs", {
    method: "POST",
    token,
    json: input,
  });
}

/** Owner only; `409 solution_run_busy` while it executes. */
export function deleteRun(token: string, runId: string, reason: string): Promise<void> {
  return apiFetch<void>(`${runBase(runId)}?reason=${encodeURIComponent(reason)}`, {
    method: "DELETE",
    token,
  });
}

export function runReportUrl(runId: string): string {
  return `${runBase(runId)}/report`;
}

/** Download the PDF report (audited server-side). */
export function downloadRunReport(token: string, runId: string, name?: string): Promise<void> {
  return downloadFile(token, runReportUrl(runId), name);
}

export function runOutputUrl(runId: string, name: string): string {
  return `${runBase(runId)}/outputs/${encodeURIComponent(name)}`;
}

/**
 * Download a file the run's kind wrote next to its report — its result names
 * it (e.g. Q&A's `docx_output`). Audited server-side
 * (`solution_run.output_downloaded`).
 */
export function downloadRunOutput(
  token: string,
  runId: string,
  name: string,
  saveAs?: string,
): Promise<void> {
  return downloadFile(token, runOutputUrl(runId, name), saveAs);
}

/**
 * Upload one file for a run with progress — XMLHttpRequest because `fetch`
 * cannot report upload progress. Errors map to the same `ApiError` shape
 * `apiFetch` throws. The returned `upload_id` goes into `upload_ids`.
 */
export function uploadForRun(
  token: string,
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<UploadRefOut> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", "/api/v1/solutions/uploads");
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.setRequestHeader("Accept", "application/json");
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
    };
    xhr.onerror = () => reject(new ApiError(0, "network_error", "Network error", null));
    xhr.onload = () => {
      let body: unknown = null;
      try {
        body = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        body = xhr.responseText;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(1);
        resolve(body as UploadRefOut);
        return;
      }
      const envelope = (body && typeof body === "object" ? body : null) as ApiErrorBody | null;
      reject(
        new ApiError(
          xhr.status,
          envelope?.error?.code ?? `http_${xhr.status}`,
          envelope?.error?.message ?? `HTTP ${xhr.status}`,
          envelope ?? (typeof body === "string" ? body : null),
        ),
      );
    };
    const form = new FormData();
    form.append("file", file);
    xhr.send(form);
  });
}
